/**
 * AUTO-BOT Store (Zustand)
 *
 * Manages automated trading bot configurations, state, and execution history.
 * Communicates with tradovate-service via the Rust backend relay.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { wsService } from '../services/WebSocketService';
import { useToastStore } from './toastStore';
import { getApiBaseUrl } from '../config/ports';
import type {
  AutoBot,
  AutoBotExecution,
  AutoBotStatus,
  BotRuntimeState,
  SignalFilter,
  BotTarget,
  BotRiskConfig,
  BotExecutionConfig,
  DistributiveOmit,
} from '@shared/types/autoBot';
import { createDefaultRuntimeState } from '@shared/types/autoBot';

// ============================================================================
// STORE STATE INTERFACE
// ============================================================================

interface AutoBotState {
  // Bots
  bots: AutoBot[];
  botsLoading: boolean;
  botsError: string | null;

  // Global Status
  globalEnabled: boolean;
  activeBots: number;
  totalBots: number;

  // Executions
  executions: AutoBotExecution[];
  executionsTotal: number;
  executionsLoading: boolean;

  // Selected Bot (for editing)
  selectedBotId: string | null;

  // Socket initialized
  initialized: boolean;
}

interface AutoBotActions {
  // Initialization
  initialize: () => void;
  cleanup: () => void;

  // Bot CRUD
  fetchBots: () => void;
  createBot: (bot: DistributiveOmit<AutoBot, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateBot: (botId: string, updates: Partial<AutoBot>) => void;
  deleteBot: (botId: string) => void;

  // Bot Control
  enableBot: (botId: string, enabled: boolean) => void;
  pauseBot: (botId: string, reason?: string) => void;
  resumeBot: (botId: string) => void;

  // Executions
  /**
   * @param quiet Suppress the error toast on failure. For BACKGROUND callers —
   *   the chart polls this every 30s purely to draw a signal-to-entry line, and
   *   a blip there should cost you a missing line, not an alert. A restart of
   *   utt-engine is enough to trip it, and deploys restart it routinely.
   */
  fetchExecutions: (botId?: string, limit?: number, offset?: number, quiet?: boolean) => void;
  clearDailyStats: (botId: string) => void;

  // Global Control
  setGlobalEnabled: (enabled: boolean) => void;
  fetchStatus: () => void;

  // UI State
  selectBot: (botId: string | null) => void;
  getBot: (botId: string) => AutoBot | undefined;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const showToast = (type: 'success' | 'error' | 'info' | 'warning', message: string) => {
  useToastStore.getState().addToast({
    type,
    message,
    duration: 4000,
  });
};

/**
 * Refetch the bot list after a short delay, guarded so it can't throw if the
 * store is mid-teardown. Used for BOS/CHoCH writes where the engine forwards to
 * tradovate-service and only returns { success: true } — the actual bot lands
 * in GET /auto-bot/bots after an async round-trip.
 */
const safeRefetch = (get: () => { fetchBots: () => void }, delay: number) => {
  setTimeout(() => {
    try {
      get().fetchBots();
    } catch (err) {
      console.warn('[AUTO-BOT Store] safeRefetch skipped (store teardown?):', err);
    }
  }, delay);
};

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useAutoBotStore = create<AutoBotState & AutoBotActions>((set, get) => ({
  // Initial State
  bots: [],
  botsLoading: false,
  botsError: null,
  globalEnabled: false,
  activeBots: 0,
  totalBots: 0,
  executions: [],
  executionsTotal: 0,
  executionsLoading: false,
  selectedBotId: null,
  initialized: false,

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  initialize: () => {
    const { initialized } = get();
    if (initialized) return;

    console.log('[AUTO-BOT Store] Initializing socket handlers');

    // Bot list response
    wsService.on('auto-bot:bots', (data: { success: boolean; bots?: AutoBot[]; error?: string }) => {
      console.log('[AUTO-BOT Store] Received auto-bot:bots', data);
      if (data.success && data.bots) {
        set({ bots: data.bots, botsLoading: false, botsError: null });
      } else {
        set({ botsError: data.error || 'Failed to fetch bots', botsLoading: false });
      }
    });

    // Bot created
    wsService.on('auto-bot:bot-created', (data: { success: boolean; bot?: AutoBot; error?: string }) => {
      console.log('[AUTO-BOT Store] Received auto-bot:bot-created', data);
      if (data.success && data.bot) {
        showToast('success', `Bot "${data.bot.name}" created`);
      } else {
        showToast('error', data.error || 'Failed to create bot');
      }
    });

    // Bot updated (broadcast to all clients)
    wsService.on('auto-bot:bot-updated', (data: { bot?: AutoBot; success?: boolean; error?: string }) => {
      if (data.bot) {
        set((state) => ({
          bots: state.bots.map((b) => (b.id === data.bot!.id ? data.bot! : b)),
        }));
      }
      if (data.success === false && data.error) {
        showToast('error', data.error);
      }
    });

    // Bot deleted (broadcast to all clients)
    wsService.on('auto-bot:bot-deleted', (data: { botId?: string; success?: boolean; error?: string }) => {
      if (data.botId) {
        set((state) => ({
          bots: state.bots.filter((b) => b.id !== data.botId),
          selectedBotId: state.selectedBotId === data.botId ? null : state.selectedBotId,
        }));
        showToast('info', 'Bot deleted');
      }
      if (data.success === false && data.error) {
        showToast('error', data.error);
      }
    });

    // Bot status change
    wsService.on('auto-bot:bot-status', (data: { success: boolean; botId?: string; status?: string; error?: string }) => {
      if (!data.success && data.error) {
        showToast('error', data.error);
      }
    });

    // Executions response
    wsService.on('auto-bot:executions', (data: { success: boolean; executions?: AutoBotExecution[]; total?: number; error?: string }) => {
      if (data.success && data.executions) {
        set({
          executions: data.executions,
          executionsTotal: data.total || data.executions.length,
          executionsLoading: false,
        });
      } else {
        set({ executionsLoading: false });
        if (data.error) {
          showToast('error', data.error);
        }
      }
    });

    // Global status response
    wsService.on('auto-bot:status', (data: AutoBotStatus) => {
      set({
        globalEnabled: data.globalEnabled,
        activeBots: data.activeBots,
        totalBots: data.totalBots,
      });
    });

    // Global enabled state changed
    wsService.on('auto-bot:global-status', (data: { enabled: boolean }) => {
      set({ globalEnabled: data.enabled });
      showToast('info', data.enabled ? 'AUTO-BOT enabled globally' : 'AUTO-BOT disabled globally');
    });

    // Real-time execution update (when a bot executes a trade)
    wsService.on('auto-bot:execution', (data: { execution: AutoBotExecution }) => {
      const execution = data.execution;
      // Add to executions list (prepend, most recent first)
      set((state) => ({
        executions: [execution, ...state.executions].slice(0, 100), // Keep last 100
        executionsTotal: state.executionsTotal + 1,
      }));

      // Update bot state with new stats
      if (execution.botId) {
        set((state) => ({
          bots: state.bots.map((bot) => {
            if (bot.id !== execution.botId) return bot;
            // Update runtime state with new stats
            const currentState = bot.state || createDefaultRuntimeState();
            return {
              ...bot,
              state: {
                ...currentState,
                dailyStats: {
                  ...currentState.dailyStats,
                  trades: currentState.dailyStats.trades + 1,
                  lastTradeTime: execution.timestamp,
                },
              },
            };
          }),
        }));
      }

      // Show toast for execution
      const actionDisplay = execution.action === 'Buy' ? '🟢 BUY' : '🔴 SELL';
      const isSuccess = execution.status === 'success';
      const result = isSuccess ? '✓' : '✗';
      showToast(
        isSuccess ? 'success' : 'error',
        `${result} AUTO-BOT: ${actionDisplay} ${execution.quantity}x${execution.symbol}${execution.fillPrice ? ` @${execution.fillPrice}` : ''}`
      );
    });

    // Circuit breaker triggered
    wsService.on('auto-bot:circuit-breaker', (data: { botId: string; reason: string; resumeAt?: number }) => {
      set((state) => ({
        bots: state.bots.map((bot) => {
          if (bot.id !== data.botId) return bot;
          const currentState = bot.state || createDefaultRuntimeState();
          return {
            ...bot,
            state: {
              ...currentState,
              status: 'circuit_breaker' as const,
              resumeAt: data.resumeAt,
            },
          };
        }),
      }));
      showToast('warning', `Circuit breaker triggered: ${data.reason}`);
    });

    // Errors
    wsService.on('auto-bot:error', (data: { message: string }) => {
      showToast('error', data.message);
    });

    set({ initialized: true });

    // Fetch initial data
    get().fetchBots();
    get().fetchStatus();
  },

  cleanup: () => {
    console.log('[AUTO-BOT Store] Cleaning up socket handlers');
    wsService.off('auto-bot:bots');
    wsService.off('auto-bot:bot-created');
    wsService.off('auto-bot:bot-updated');
    wsService.off('auto-bot:bot-deleted');
    wsService.off('auto-bot:bot-status');
    wsService.off('auto-bot:executions');
    wsService.off('auto-bot:execution');
    wsService.off('auto-bot:circuit-breaker');
    wsService.off('auto-bot:status');
    wsService.off('auto-bot:global-status');
    wsService.off('auto-bot:error');
    set({ initialized: false });
  },

  // ============================================================================
  // BOT CRUD
  // ============================================================================

  fetchBots: async () => {
    console.log('[AUTO-BOT Store] fetchBots called');
    set({ botsLoading: true, botsError: null });
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/bots`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Invalid JSON response from server');
      }

      if (data.success && Array.isArray(data.bots)) {
        // Replace the list wholesale (merged classic + BOS from the engine).
        // De-dupe by id defensively so classic/BOS entries never double up.
        const seen = new Set<string>();
        const deduped = (data.bots as AutoBot[]).filter((b) => {
          if (seen.has(b.id)) return false;
          seen.add(b.id);
          return true;
        });
        set({ bots: deduped, botsLoading: false });
      } else {
        const errorMsg = data?.error || 'Failed to fetch bots';
        set({ botsError: errorMsg, botsLoading: false });
        showToast('error', errorMsg);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Network error';
      console.error('Failed to fetch bots:', error);
      set({ botsError: errorMsg, botsLoading: false });
      showToast('error', `Failed to fetch bots: ${errorMsg}`);
    }
  },

  createBot: async (bot) => {
    console.log('[AUTO-BOT Store] createBot called', bot);

    // Ensure signalFilter.actions is always present (required by Rust backend).
    // BOS/CHoCH bots have no signalFilter — skip this classic-only guard.
    if (bot.signalSource !== 'break_of_structure') {
      if (!bot.signalFilter.actions || bot.signalFilter.actions.length === 0) {
        showToast('error', 'At least one signal action (BUY/SELL) must be selected');
        return;
      }
    }

    console.log('[AUTO-BOT Store] Sending bot data:', JSON.stringify(bot, null, 2));

    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/bots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bot),
      });

      const data = await response.json();

      if (!data.success) {
        showToast('error', data.error || 'Failed to create bot');
        return;
      }

      // CLASSIC bots: the engine returns the created bot object. Keep the
      // existing optimistic behavior byte-identical (add + enable-on-save).
      if (data.bot) {
        // Add to local state
        set((state) => ({
          bots: [...state.bots, data.bot],
        }));

        showToast('success', 'Bot created');

        // If "Enable on save" was checked, enable the bot now
        // (Backend creates bots disabled for safety)
        if (bot.enabled && data.bot?.id) {
          console.log('[AUTO-BOT Store] Enabling bot after creation:', data.bot.id);
          get().enableBot(data.bot.id, true);
        }
      } else {
        // BOS/CHoCH bots: the engine forwarded the write to tradovate-service and
        // returned only { success: true } — no bot object. The bot is created
        // asynchronously (disabled by design) and merged into GET /auto-bot/bots
        // after a short round-trip. Refetch to pick it up; do NOT auto-enable
        // even if the editor passed enabled:true (backend forces disabled).
        showToast('success', 'bot created (disabled) — enable it from the panel');
        safeRefetch(get, 500);
        safeRefetch(get, 1500);
      }
    } catch (error) {
      console.error('Failed to create bot:', error);
      showToast('error', 'Network error - failed to create bot');
    }
  },

  updateBot: async (botId, updates) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/${botId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      const data = await response.json();

      if (!data.success) {
        showToast('error', data.error || 'Failed to update bot');
        return;
      }

      // CLASSIC: engine returns the updated bot — optimistic in-place update.
      // BOS: no bot returned (async forward) — refetch to reconcile.
      if (data.bot) {
        set((state) => ({
          bots: state.bots.map((b) => (b.id === botId ? data.bot : b)),
        }));
      } else {
        safeRefetch(get, 400);
      }

      showToast('success', 'Bot updated');
    } catch (error) {
      console.error('Failed to update bot:', error);
      showToast('error', 'Network error - failed to update bot');
    }
  },

  deleteBot: async (botId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/${botId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!data.success) {
        showToast('error', data.error || 'Failed to delete bot');
        return;
      }

      // Remove locally by id (works for both classic and BOS — no bot object
      // is needed for a delete).
      set((state) => ({
        bots: state.bots.filter((b) => b.id !== botId),
        selectedBotId: state.selectedBotId === botId ? null : state.selectedBotId,
      }));

      showToast('success', 'Bot deleted');

      // Reconcile with the server: BOS deletes are applied async on
      // tradovate-service, then merged back into GET /auto-bot/bots.
      safeRefetch(get, 400);
    } catch (error) {
      console.error('Failed to delete bot:', error);
      showToast('error', 'Network error - failed to delete bot');
    }
  },

  // ============================================================================
  // BOT CONTROL
  // ============================================================================

  enableBot: async (botId, enabled) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/enable`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bot_id: botId, enabled }),
      });

      const data = await response.json();

      if (!data.success) {
        showToast('error', data.error || 'Failed to update bot');
        return;
      }

      // CLASSIC: engine returns the updated bot — optimistic in-place update.
      // BOS: no bot returned (async forward) — refetch so the card reflects the
      // new enabled/halted state.
      if (data.bot) {
        set((state) => ({
          bots: state.bots.map((b) => (b.id === botId ? data.bot : b)),
        }));
      } else {
        // The BOS enable/disable round-trips async through the engine->tradovate
        // relay; refetch at staggered delays so the card reflects the final
        // status even if the relay is slow (a single 400ms refetch can land
        // before tradovate has applied the change).
        safeRefetch(get, 400);
        safeRefetch(get, 1500);
      }

      showToast('success', `Bot ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to enable/disable bot:', error);
      showToast('error', 'Network error - failed to update bot');
    }
  },

  pauseBot: async (botId, reason) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/${botId}/pause`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });

      const data = await response.json();

      if (!data.success) {
        showToast('error', data.error || 'Failed to pause bot');
        return;
      }

      // Update local state
      set((state) => ({
        bots: state.bots.map((b) => (b.id === botId ? data.bot : b)),
      }));

      showToast('success', 'Bot paused');
    } catch (error) {
      console.error('Failed to pause bot:', error);
      showToast('error', 'Network error - failed to pause bot');
    }
  },

  resumeBot: async (botId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/${botId}/resume`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        showToast('error', data.error || 'Failed to resume bot');
        return;
      }

      // Update local state
      set((state) => ({
        bots: state.bots.map((b) => (b.id === botId ? data.bot : b)),
      }));

      showToast('success', 'Bot resumed');
    } catch (error) {
      console.error('Failed to resume bot:', error);
      showToast('error', 'Network error - failed to resume bot');
    }
  },

  // ============================================================================
  // EXECUTIONS
  // ============================================================================

  fetchExecutions: async (botId, limit = 50, offset = 0, quiet = false) => {
    set({ executionsLoading: true });
    try {
      const params = new URLSearchParams();
      if (botId) params.append('bot_id', botId);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await fetch(`${getApiBaseUrl()}/auto-bot/executions?${params}`);
      const data = await response.json();

      if (data.success) {
        set({ executions: data.executions, executionsLoading: false });
      } else {
        set({ executionsLoading: false });
        if (!quiet) showToast('error', data.error || 'Failed to fetch executions');
      }
    } catch (error) {
      console.error('Failed to fetch executions:', error);
      set({ executionsLoading: false });
      // Left on the console either way; only the interruption is conditional.
      if (!quiet) showToast('error', 'Network error - failed to fetch executions');
    }
  },

  clearDailyStats: async (botId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/${botId}/clear-stats`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        showToast('error', data.error || 'Failed to clear stats');
        return;
      }

      // Update local state
      set((state) => ({
        bots: state.bots.map((b) => (b.id === botId ? data.bot : b)),
      }));

      showToast('success', 'Daily stats cleared');
    } catch (error) {
      console.error('Failed to clear stats:', error);
      showToast('error', 'Network error - failed to clear stats');
    }
  },

  // ============================================================================
  // GLOBAL CONTROL
  // ============================================================================

  setGlobalEnabled: async (enabled) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/global-enabled`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });

      const data = await response.json();

      if (!data.success) {
        showToast('error', data.error || 'Failed to update global enabled');
        return;
      }

      // Update local state
      set({ globalEnabled: enabled });

      showToast('success', `AUTO-BOT ${enabled ? 'enabled' : 'disabled'} globally`);
    } catch (error) {
      console.error('Failed to set global enabled:', error);
      showToast('error', 'Network error - failed to update global enabled');
    }
  },

  fetchStatus: async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auto-bot/status`);
      const data = await response.json();

      if (data.success) {
        set({
          globalEnabled: data.globalEnabled,
          activeBots: data.activeBots,
          totalBots: data.totalBots,
        });
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  },

  // ============================================================================
  // UI STATE
  // ============================================================================

  selectBot: (botId) => {
    set({ selectedBotId: botId });
  },

  getBot: (botId) => {
    return get().bots.find((b) => b.id === botId);
  },
}));

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Get enabled bots only
 */
export const useEnabledBots = () =>
  useAutoBotStore(useShallow((state) => state.bots.filter((b) => b.enabled)));

/**
 * Get active bots (enabled + status === 'active')
 */
export const useActiveBots = () =>
  useAutoBotStore(
    useShallow((state) =>
      state.bots.filter((b) => b.enabled && b.state?.status === 'active')
    )
  );

/**
 * Get bot by ID
 */
export const useBotById = (botId: string | null) =>
  useAutoBotStore((state) => (botId ? state.bots.find((b) => b.id === botId) : undefined));

/**
 * Get selected bot
 */
export const useSelectedBot = () =>
  useAutoBotStore((state) =>
    state.selectedBotId ? state.bots.find((b) => b.id === state.selectedBotId) : undefined
  );

/**
 * Get executions for a specific bot
 */
export const useBotExecutions = (botId: string) =>
  useAutoBotStore((state) =>
    state.executions.filter((e) => e.botId === botId)
  );

/**
 * Get all executions - consumers should slice/filter as needed with useMemo
 */
export const useExecutions = () =>
  useAutoBotStore((state) => state.executions);

/**
 * Get global AUTO-BOT status - individual selectors to prevent object reference issues
 */
export const useAutoBotGlobalEnabled = () =>
  useAutoBotStore((state) => state.globalEnabled);

export const useAutoBotActiveBots = () =>
  useAutoBotStore((state) => state.activeBots);

export const useAutoBotTotalBots = () =>
  useAutoBotStore((state) => state.totalBots);

/**
 * Get global AUTO-BOT status as object (use with caution - may cause re-renders)
 */
export const useAutoBotStatus = () => {
  const globalEnabled = useAutoBotGlobalEnabled();
  const activeBots = useAutoBotActiveBots();
  const totalBots = useAutoBotTotalBots();
  return { globalEnabled, activeBots, totalBots };
};

// ============================================================================
// INITIALIZATION HOOK
// ============================================================================

/**
 * Hook to initialize AUTO-BOT store on mount.
 * Use this in a top-level component or once in the app.
 * Waits for socket to be connected before initializing handlers.
 */
export function initializeAutoBotStore() {
  const store = useAutoBotStore.getState();
  if (store.initialized) return;

  // Check if socket is connected
  if (wsService.isConnected()) {
    console.log('[AUTO-BOT Store] Socket connected, initializing immediately');
    store.initialize();
  } else {
    // Wait for socket to connect
    console.log('[AUTO-BOT Store] Socket not connected, waiting...');
    const checkInterval = setInterval(() => {
      if (wsService.isConnected()) {
        console.log('[AUTO-BOT Store] Socket now connected, initializing');
        clearInterval(checkInterval);
        const currentStore = useAutoBotStore.getState();
        if (!currentStore.initialized) {
          currentStore.initialize();
        }
      }
    }, 500);

    // Give up after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      const currentStore = useAutoBotStore.getState();
      if (!currentStore.initialized) {
        console.warn('[AUTO-BOT Store] Socket connection timeout, initializing anyway');
        currentStore.initialize();
      }
    }, 10000);
  }
}
